import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { CURRENCY_META } from "../lib/currencyMeta";

/**
 * Display-only currency conversion. All backend transactions stay in AED
 * (Stripe charges are always in AED regardless of what the customer picks
 * here) — this context exists solely to let international browsers eyeball
 * prices in their local currency.
 *
 * Rates are fetched once on mount from `/api/fx/rates` (server caches 6h,
 * falls back to a hardcoded snapshot if the upstream is down). The user's
 * pick persists to localStorage so they don't need to re-select on every
 * visit.
 */
const CurrencyCtx = createContext(null);

const STORAGE_KEY = "mazad_currency";
const DEFAULT_CURRENCY = "AED";

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [currency, setCurrencyState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved || DEFAULT_CURRENCY;
    } catch {
      return DEFAULT_CURRENCY;
    }
  });
  const [rates, setRates] = useState({ AED: 1 });
  const [fetchedAt, setFetchedAt] = useState(null);
  const [source, setSource] = useState("initial");
  // Tracks whether the current in-memory currency came from the user's
  // saved profile. Prevents the server-persist effect from writing back
  // the exact same value we just loaded (avoids a redundant PUT + audit-
  // trail noise on every mount).
  const lastServerValue = useRef(null);

  // Fetch rates once on mount. Failing silently is fine — we keep the
  // AED-only rates map and the switcher still renders AED cleanly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/fx/rates");
        if (cancelled) return;
        setRates(data.rates || { AED: 1 });
        setFetchedAt(data.fetched_at || null);
        setSource(data.source || "unknown");
      } catch {
        /* keep AED-only fallback so the UI never breaks */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When the logged-in user loads (or changes), adopt their saved
  // preferred_currency if present. This wins over the anonymous
  // localStorage cache so a user gets a consistent currency across
  // devices without needing to re-pick it.
  useEffect(() => {
    if (user?.preferred_currency && typeof user.preferred_currency === "string") {
      const code = user.preferred_currency.toUpperCase();
      lastServerValue.current = code;
      setCurrencyState(code);
      try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
    } else if (!user) {
      // On sign-out, reset the pointer so the next login persists again.
      lastServerValue.current = null;
    }
  }, [user]);

  const setCurrency = useCallback(async (c) => {
    const code = (c || DEFAULT_CURRENCY).toUpperCase();
    setCurrencyState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* private mode / quota */ }
    // If signed in and the pick differs from the server value, persist it.
    // Fire-and-forget — a failure here shouldn't break the UI update.
    if (user && code !== lastServerValue.current) {
      lastServerValue.current = code;
      try {
        await api.put("/me/preferred-currency", { preferred_currency: code });
      } catch {
        /* best-effort — the local pick remains; syncs on next explicit change */
      }
    }
  }, [user]);

  const value = useMemo(() => {
    const rate = rates[currency] ?? (currency === "AED" ? 1 : null);
    const meta = CURRENCY_META[currency] || { code: currency, symbol: currency, flag: "", name: currency, decimals: 2 };

    // Convert an AED number to the currently-selected currency. If the
    // rate is missing (very rare — user picked something we don't have),
    // fall back to AED so nothing displays as `NaN`.
    const convert = (aedAmount) => {
      const n = Number(aedAmount);
      if (!isFinite(n)) return 0;
      if (!rate) return n;
      return n * rate;
    };

    // Format a number in the currently-selected currency. Uses
    // `Intl.NumberFormat` so grouping / decimals follow the locale of the
    // currency (e.g. INR gets Indian grouping, JPY gets zero decimals).
    const format = (aedAmount, opts = {}) => {
      const converted = convert(aedAmount);
      const decimals = opts.decimals ?? (meta.decimals ?? 2);
      // For very large numbers keep 0 decimals to save space — e.g.
      // "AED 1,250,000" not "AED 1,250,000.00". JPY / IDR / KRW already
      // define decimals=0 in the meta.
      const finalDecimals = Math.abs(converted) >= 1000 ? 0 : decimals;
      try {
        const nf = new Intl.NumberFormat(undefined, {
          minimumFractionDigits: finalDecimals,
          maximumFractionDigits: finalDecimals,
        });
        return `${meta.symbol} ${nf.format(converted)}`;
      } catch {
        return `${meta.symbol} ${converted.toFixed(finalDecimals)}`;
      }
    };

    // The full sorted list of currencies the UI can offer. Pinned +
    // sorted alphabetically by code so the picker is scannable. Rates
    // from the server come with 160+ entries — we intersect with our
    // CURRENCY_META to keep flags + display names accurate.
    const availableCurrencies = Object.keys(CURRENCY_META)
      .filter((c) => rates[c] != null || c === "AED")
      .sort((a, b) => {
        // AED pinned at the top since it's the platform base.
        if (a === "AED") return -1;
        if (b === "AED") return 1;
        return a.localeCompare(b);
      })
      .map((code) => ({ code, ...CURRENCY_META[code] }));

    return {
      currency,
      setCurrency,
      rates,
      rate,
      source,
      fetchedAt,
      convert,
      format,
      availableCurrencies,
      meta,
      isAED: currency === "AED",
    };
  }, [currency, rates, source, fetchedAt, setCurrency]);

  return <CurrencyCtx.Provider value={value}>{children}</CurrencyCtx.Provider>;
}

export const useCurrency = () => {
  const ctx = useContext(CurrencyCtx);
  if (!ctx) {
    // Defensive: if a component renders outside the provider, return a
    // no-op AED formatter so nothing crashes.
    return {
      currency: "AED",
      setCurrency: () => {},
      rates: { AED: 1 },
      rate: 1,
      convert: (n) => Number(n) || 0,
      format: (n) => `AED ${Math.round(Number(n) || 0).toLocaleString()}`,
      availableCurrencies: [],
      meta: { symbol: "AED", flag: "", name: "UAE Dirham", decimals: 2 },
      isAED: true,
    };
  }
  return ctx;
};
